import React from 'react';
import { 
  Activity, 
  RefreshCw
} from 'lucide-react';
import { SearchSelect } from '../../components/shared';
import { useAdminPerformance } from '../../hooks/useAdminPerformance';
import { useAdminInfrastructure } from '../../hooks/useAdminInfrastructure';
import InfrastructurePanel from '../../components/admin/InfrastructurePanel';

const AdminPerformance = () => {
  // Use the custom hook for performance data
  const {
    performance,
    isLoading,
    error,
    period,
    setPeriod,
    periodOptions,
    refetch
  } = useAdminPerformance('month');

  const {
    infrastructure,
    isLoading: infraLoading,
    refetch: refetchInfra,
  } = useAdminInfrastructure();

  // Helper function to safely parse numeric values
  const getNumeric = (val) => {
    if (val === null || val === undefined || val === '') return 0;
    const strVal = String(val).trim();
    if (strVal === '' || strVal === 'null' || strVal === 'undefined' || strVal === 'NaN') {
      return 0;
    }
    const cleaned = strVal.replace(/[^0-9.-]/g, '');
    if (cleaned === '' || cleaned === '-' || cleaned === '.') {
      return 0;
    }
    const parsed = parseFloat(cleaned);
    if (isNaN(parsed) || !isFinite(parsed)) {
      return 0;
    }
    return parsed;
  };

  const getStatusBadge = (status) => {
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
        status === 'healthy' || status === 'operational' || status === 'good' || status === 'normal'
          ? 'bg-green-100 text-green-800'
          : status === 'warning'
          ? 'bg-yellow-100 text-yellow-800'
          : 'bg-red-100 text-red-800'
      }`}>
        {status}
      </span>
    );
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
        <div className="bg-white border rounded-lg p-6">
          <div className="text-center">
            <p className="text-red-600">Failed to load performance data. Please try again.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-4">
        {/* Header */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Activity className="w-6 h-6" />
                Performance
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Monitor platform performance and system health
              </p>
            </div>
            <div className="flex items-center gap-2">
              <SearchSelect
                value={period}
                onChange={setPeriod}
                options={periodOptions}
                className="w-40"
              />
              <button
                onClick={() => refetch()}
                className="p-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="bg-white border rounded-lg p-6">
            <div className="text-center">Loading performance data...</div>
          </div>
        ) : (
          <>
            <InfrastructurePanel
              infrastructure={infrastructure}
              isLoading={infraLoading}
              onRefresh={refetchInfra}
            />

            {/* System Metrics */}
            {performance.system_metrics && (
              <div className="bg-white border rounded-lg p-3 sm:p-4">
                <h2 className="text-lg font-bold text-gray-900 mb-4">System Metrics</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Uptime */}
                  {performance.system_metrics.uptime && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-700">Uptime</span>
                        {getStatusBadge(performance.system_metrics.uptime.status)}
                      </div>
                      <p className="text-2xl font-bold">
                        {performance.system_metrics.uptime.percentage}%
                      </p>
                    </div>
                  )}

                  {/* Response Time */}
                  {performance.system_metrics.response_time && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-700">Response Time</span>
                        {getStatusBadge(performance.system_metrics.response_time.status)}
                      </div>
                      <p className="text-2xl font-bold">
                        {performance.system_metrics.response_time.average}ms
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        P95: {performance.system_metrics.response_time.p95}ms
                      </p>
                    </div>
                  )}

                  {/* Server Resources */}
                  {performance.system_metrics.server_resources && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-700">Server Resources</span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-xs text-gray-600">CPU</span>
                          <span className="text-xs font-medium">
                            {performance.system_metrics.server_resources.cpu?.usage}%
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-xs text-gray-600">Memory</span>
                          <span className="text-xs font-medium">
                            {performance.system_metrics.server_resources.memory?.usage}%
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-xs text-gray-600">Disk</span>
                          <span className="text-xs font-medium">
                            {performance.system_metrics.server_resources.disk?.usage}%
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Database */}
                  {performance.system_metrics.database && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-700">Database</span>
                        {getStatusBadge(performance.system_metrics.database.status)}
                      </div>
                      <p className="text-sm text-gray-600">
                        Connections: {performance.system_metrics.database.connections}
                      </p>
                      <p className="text-sm text-gray-600">
                        Query Time: {performance.system_metrics.database.query_time}ms
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Application Metrics */}
            {performance.application_metrics && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div className="bg-white border rounded-lg p-3 sm:p-4">
                  <h2 className="text-lg font-bold text-gray-900 mb-4">Payment Processing</h2>
                  {performance.application_metrics.payment_processing && (
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Total Payments</span>
                        <span className="text-sm font-medium">
                          {performance.application_metrics.payment_processing.total_payments || 0}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Successful</span>
                        <span className="text-sm font-medium text-green-600">
                          {performance.application_metrics.payment_processing.successful || 0}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Failed</span>
                        <span className="text-sm font-medium text-red-600">
                          {performance.application_metrics.payment_processing.failed || 0}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Success Rate</span>
                        <span className="text-sm font-bold">
                          {getNumeric(performance.application_metrics.payment_processing.success_rate).toFixed(2)}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-white border rounded-lg p-3 sm:p-4">
                  <h2 className="text-lg font-bold text-gray-900 mb-4">Booking Processing</h2>
                  {performance.application_metrics.booking_processing && (
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Total Bookings</span>
                        <span className="text-sm font-medium">
                          {performance.application_metrics.booking_processing.total_bookings || 0}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Completed</span>
                        <span className="text-sm font-medium text-green-600">
                          {performance.application_metrics.booking_processing.completed || 0}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Cancelled</span>
                        <span className="text-sm font-medium text-red-600">
                          {performance.application_metrics.booking_processing.cancelled || 0}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Completion Rate</span>
                        <span className="text-sm font-bold">
                          {getNumeric(performance.application_metrics.booking_processing.completion_rate).toFixed(2)}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Business Metrics */}
            {performance.business_metrics && (
              <div className="bg-white border rounded-lg p-3 sm:p-4">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Business Metrics</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="p-3 bg-gray-50 rounded">
                    <p className="text-xs text-gray-600">Conversion Rate</p>
                    <p className="text-lg font-bold">
                      {getNumeric(performance.business_metrics.conversion_rate).toFixed(2)}%
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded">
                    <p className="text-xs text-gray-600">Avg Booking Value</p>
                    <p className="text-lg font-bold">
                      ${getNumeric(performance.business_metrics.average_booking_value).toFixed(2)}
                    </p>
                  </div>
                  {performance.business_metrics.customer_satisfaction && (
                    <div className="p-3 bg-gray-50 rounded">
                      <p className="text-xs text-gray-600">Avg Rating</p>
                      <p className="text-lg font-bold">
                        {getNumeric(performance.business_metrics.customer_satisfaction.average_rating).toFixed(1)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {performance.business_metrics.customer_satisfaction.total_reviews || 0} reviews
                      </p>
                    </div>
                  )}
                  {performance.business_metrics.vendor_performance && (
                    <div className="p-3 bg-gray-50 rounded">
                      <p className="text-xs text-gray-600">Active Vendors</p>
                      <p className="text-lg font-bold">
                        {performance.business_metrics.vendor_performance.active_vendors || 0}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {performance.vendor_leaderboard?.length > 0 && (
              <div className="bg-white border rounded-lg p-3 sm:p-4">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Vendor performance leaderboard</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Composite score from completion rate, ratings, booking volume, and cancellations (last 90 days).
                </p>
                <div className="space-y-2">
                  {performance.vendor_leaderboard.map((vendor, index) => (
                    <div
                      key={vendor.id}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-sm font-bold text-gray-400 w-6">#{index + 1}</span>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{vendor.business_name}</p>
                          <p className="text-xs text-gray-500 truncate">{vendor.location || '—'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm shrink-0">
                        <span className="font-bold text-primary-600 tabular-nums">
                          {getNumeric(vendor.performance_score).toFixed(1)}
                        </span>
                        <span className="text-gray-500 tabular-nums">
                          {getNumeric(vendor.completion_rate).toFixed(0)}% complete
                        </span>
                        <span className="text-gray-500 tabular-nums">
                          ★ {getNumeric(vendor.average_rating).toFixed(1)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPerformance;

